import { useState, useRef, useEffect } from "react";
import Icon from "../Icon";
import { usePosicionDesplegable } from "../posicionDesplegable";

const MESES_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DIAS_ES  = ["L","M","X","J","V","S","D"];

// refDate: fecha de referencia para abrir el calendario cuando aún no hay valor.
// La usa el "Hasta" de un rango para partir del mes del "Desde" en vez del mes
// actual, que puede estar a un año de distancia.
// Los cinco añadidos de abajo no son adornos: son lo que le faltaba para poder
// sustituir a los <input type="date"> nativos que habia repartidos por la app.
// Cada uno lo pedia un sitio real, y sin ellos la sustitucion habria perdido
// comportamiento en silencio, que es lo peor que puede pasar en un formulario.
//
//   disabled   el panel del paso y la ficha del proyecto desactivan la fecha a
//              quien no puede replanificar. Sin esto, un campo de solo lectura
//              se habria vuelto editable sin que nadie lo notara hasta que el
//              servidor devolviera un 403.
//   min/max    los filtros de rango se acotaban entre si para que no se pueda
//              pedir un "desde" posterior al "hasta", que no devuelve nada.
//   invalido   los formularios pintan el borde en rojo cuando las fechas van al
//              reves. El `style` no servia: va al contenedor, no al boton.
//   nombre     el nativo tenia un <label> al lado; un <button> no se asocia a
//              una etiqueta, asi que el nombre del campo se pasa a mano para el
//              lector de pantalla.
//   margenInf. el boton reservaba 11 px por debajo, que en una rejilla de dos
//              columnas descuadra la fila. Por omision se queda como estaba.
export default function DatePicker({value, onChange, style={}, placeholder="Seleccionar", upward=false, time="", onTimeChange=null, compact=false, refDate="", disabled=false, min="", max="", invalido=false, nombre="", margenInferior=null, id, "aria-labelledby":etiquetadoPor}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  // Donde se despliega: debajo o encima del campo, y corrido hacia dentro si se saldria por un
  // lado, segun el hueco REAL (la ventana y el modal o contenedor con scroll que lo envuelva),
  // y se rehace al redimensionar o hacer scroll. Antes solo se miraba el hueco vertical con un
  // alto supuesto de 330, y un campo en una columna de la derecha abria el calendario medio fuera.
  // El panel se monta 8px sobre el campo (separacion -8), como siempre.
  const { estilo: posicionPanel } = usePosicionDesplegable(open, ref, panelRef, { preferirArriba: upward, separacion: -8 });

  const alternar = () => {
    if (disabled) return;
    setOpen(o => !o);
  };

  // value es "yyyy-mm-dd" o ""
  const parsed = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value+"T00:00:00") : null;
  const refParsed = refDate && /^\d{4}-\d{2}-\d{2}$/.test(refDate) ? new Date(refDate+"T00:00:00") : null;
  const today_ = new Date(); today_.setHours(0,0,0,0);

  const [cal, setCal] = useState(() => {
    const d = parsed || refParsed || today_;
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  // Actualizar el mes visible cuando cambia el value externo
  useEffect(() => {
    if (parsed) setCal({ y: parsed.getFullYear(), m: parsed.getMonth() });
  }, [value]);

  // Sin valor propio, seguir a la fecha de referencia. Solo mientras está
  // cerrado: si no, cambiar el "Desde" con el desplegable abierto daría un salto
  // de mes bajo el cursor.
  useEffect(() => {
    if (!parsed && refParsed && !open) setCal({ y: refParsed.getFullYear(), m: refParsed.getMonth() });
  }, [refDate]); // eslint-disable-line

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const prevMonth = () => setCal(c => c.m === 0 ? {y:c.y-1,m:11} : {y:c.y,m:c.m-1});
  const nextMonth = () => setCal(c => c.m === 11 ? {y:c.y+1,m:0} : {y:c.y,m:c.m+1});

  const selectDay = (d) => {
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const dd = String(d.getDate()).padStart(2,"0");
    onChange(`${d.getFullYear()}-${mm}-${dd}`);
    setOpen(false);
  };

  const setToday = () => { selectDay(today_); };

  const clearDate = (e) => { e.stopPropagation(); onChange(""); setOpen(false); };

  // Construir días del mes
  const buildDays = () => {
    const first = new Date(cal.y, cal.m, 1);
    let dow = first.getDay(); // 0=dom
    dow = dow === 0 ? 6 : dow - 1; // convertir a L=0
    const days = [];
    for (let i = 0; i < dow; i++) days.push(null);
    const total = new Date(cal.y, cal.m+1, 0).getDate();
    for (let i = 1; i <= total; i++) days.push(new Date(cal.y, cal.m, i));
    return days;
  };

  const fmtDisplay = (d) => {
    if (!d) return null;
    return `${d.getDate()} ${MESES_ES[d.getMonth()].slice(0,3).toLowerCase()} ${d.getFullYear()}`;
  };

  const isSameDay = (a, b) => a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();

  // Fuera de rango. Se compara en texto ISO y no con Date porque asi no hay
  // husos ni horas de por medio: "2026-03-01" < "2026-03-02" siempre.
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const fuera = (d) => { const t = iso(d); return (min && t < min) || (max && t > max); };

  return (
    <div ref={ref} style={{position:"relative",...style}}>
      {/* El boton de abrir y el de vaciar son HERMANOS: antes el × era un span dentro del
          boton, un control dentro de otro, que no se podia operar con el teclado. El
          margen inferior pasa a la caja que los envuelve para que el maquetado no cambie. */}
      <div style={{position:"relative",marginBottom: margenInferior !== null ? margenInferior : (compact?0:11)}}>
      <button type="button" onClick={alternar} disabled={disabled}
        id={id} aria-labelledby={etiquetadoPor ? etiquetadoPor+" "+id : undefined} aria-label={etiquetadoPor ? undefined : (nombre || placeholder)} aria-expanded={open}
        style={{width:"100%",padding:compact?"5px 9px":"8px 11px",borderRadius:compact?7:8,
          border:`1px solid ${invalido ? "var(--red)" : "var(--border-ctl)"}`,
          fontSize:compact?11.5:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",
          color: parsed ? "var(--text)" : "var(--text3)",
          cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1,
          textAlign:"left",display:"flex",alignItems:"center",justifyContent:"space-between",
          paddingRight: parsed && !disabled ? (compact?26:30) : undefined,
          margin:0, gap:6}}>
        <span style={{display:"flex",alignItems:"center",gap:6}}>
          <Icon name="calendar3" size={compact?11:13} color={parsed?"var(--accent)":"var(--text3)"}/>
          {parsed ? fmtDisplay(parsed) : <span style={{fontStyle:"italic",fontSize:compact?11:12}}>{placeholder}</span>}
        </span>
        {!(parsed && !disabled) && !disabled && <Icon name="chevron-down" size={11} color="var(--text3)"/>}
      </button>
      {parsed && !disabled && (
        <button type="button" onClick={clearDate} title="Vaciar la fecha" aria-label={nombre ? `Vaciar ${nombre}` : "Vaciar la fecha"}
          style={{position:"absolute",top:"50%",right:compact?5:7,transform:"translateY(-50%)",
            background:"none",border:"none",cursor:"pointer",fontSize:15,lineHeight:1,
            color:"var(--text3)",padding:"2px 5px",borderRadius:5,fontFamily:"var(--font)"}}>×</button>
      )}
      </div>
      {onTimeChange !== null && (
        <input aria-label={nombre ? nombre + " (hora)" : "Hora"}
          type="time"
          value={time}
          onChange={e => onTimeChange(e.target.value)}
          step="60"
          style={{width:"100%",padding:"6px 11px",borderRadius:8,border:"1px solid var(--border-ctl)",
            fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",
            color: time ? "var(--text)" : "var(--text3)",marginBottom:11,cursor:"pointer"}}
        />
      )}
      {open && (
        <div ref={panelRef} style={{...posicionPanel,zIndex:600,
          background:"var(--surface)",border:"1px solid var(--border)",borderRadius:12,
          boxShadow:"var(--shadow-lg)",padding:12,minWidth:240,animation:"scaleIn .15s ease both"}}>
          {/* Cabecera mes */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <button type="button" onClick={prevMonth} style={{background:"none",border:"none",cursor:"pointer",padding:4,borderRadius:6,color:"var(--text2)",fontSize:16,lineHeight:1}}>‹</button>
            <span style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>{MESES_ES[cal.m]} {cal.y}</span>
            <button type="button" onClick={nextMonth} style={{background:"none",border:"none",cursor:"pointer",padding:4,borderRadius:6,color:"var(--text2)",fontSize:16,lineHeight:1}}>›</button>
          </div>
          {/* Cabecera días */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:4}}>
            {DIAS_ES.map(d=><div key={d} style={{textAlign:"center",fontSize:10,fontWeight:700,color:"var(--text3)",padding:"2px 0"}}>{d}</div>)}
          </div>
          {/* Días */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {buildDays().map((d,i)=>{
              if (!d) return <div key={"e"+i}/>;
              const isToday   = isSameDay(d, today_);
              const isSelected = isSameDay(d, parsed);
              const vetado = fuera(d);
              return (
                <button key={i} type="button" onClick={()=>selectDay(d)} disabled={vetado}
                  title={vetado ? "Fuera del rango permitido" : undefined}
                  style={{padding:"5px 0",borderRadius:6,border:"none",
                    cursor: vetado ? "default" : "pointer", fontSize:11.5,
                    opacity: vetado ? 0.3 : 1,
                    fontFamily:"var(--font)",fontWeight:isSelected||isToday?700:400,
                    background: isSelected?"var(--accent)": isToday&&!vetado?"var(--accent-bg)":"transparent",
                    color: isSelected?"var(--on-accent)": isToday&&!vetado?"var(--accent)":"var(--text2)"}}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          {/* Pie */}
          {/* "Hoy" no se ofrece si hoy esta fuera del rango: un atajo que no
              puede funcionar es peor que no tener atajo. */}
          {!fuera(today_) && (
            <div style={{marginTop:8,borderTop:"1px solid var(--border2)",paddingTop:8,textAlign:"center"}}>
              <button type="button" onClick={setToday}
                style={{background:"none",border:"none",cursor:"pointer",color:"var(--accent)",fontSize:12,fontWeight:600,fontFamily:"var(--font)"}}>
                Hoy
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

